//
// README - Beta Component
//
// This component is marked "beta" and will go through some iterations
// before landing at a final solution. Please reach out to the Cadence team
// if you intend to use this in the meantime and we'll help you out!
//

import React from 'react';
import { RCSkeleton, RCEyebrow, RCImage } from '../';

interface LinkElementProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  className?: string;
  children?: React.ReactNode;
}

interface RCProductCardProps {
  title: string;
  eyebrow: string;
  imageUrl: string;
  details?: string | JSX.Element;
  ctaText?: string;
  violator?: string | JSX.Element;

  /** The link element (e.g. CoreLink)
   * Example: `linkElement={(children) => (<CoreLink to="/whatever/url/">{children}</CoreLink>)}`
  */
  linkElement?: (children: React.ReactNode) => React.ReactElement<LinkElementProps>;
}

export function RCProductCard({
  title,
  eyebrow,
  imageUrl,
  details = undefined,
  ctaText = undefined,
  violator = undefined,
  linkElement,
}: RCProductCardProps) {
  function PassedLink({ children, className, ...passthroughProps }) {
    if (!linkElement) {
      return <>{children}</>;
    }
    return React.cloneElement(
      linkElement(children),
      {
        className: className,
        ...passthroughProps,
      },
    );
  }

  return (
    <div
      className="rc-product-card"
      data-rc-image-overlay-trigger
    >
      <PassedLink className="rc-product-card__link">
        <div className="rc-product-card__image-wrapper">
          <RCImage
            src={imageUrl}
            overlay="offwhiteWithHover"
          />
        </div>
        <div className="rc-product-card__content">
          <div className="rc-product-card__primary">
            <div className="rc-product-card__eyebrow">
              <RCEyebrow text={eyebrow} truncate />
            </div>
            <div
              className="rc-product-card__title"
              title={title}
            >
              {title}
            </div>
          </div>
          <div className="rc-product-card__secondary">
            {violator &&
              <div className="rc-product-card__violator">
                {violator}
              </div>
            }
            {details &&
              <div className="rc-product-card__details">
                {details}
              </div>
            }
            {ctaText &&
              <div className="rc-product-card__cta">
                {ctaText}
              </div>
            }
          </div>
        </div>
      </PassedLink>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="rc-product-card">
      <div className="rc-product-card__image-wrapper">
        <RCSkeleton width="100%" height="100%" />
      </div>
      <div className="rc-product-card__content">
        <div className="rc-product-card__primary">
          <div className="rc-product-card__title">
            <RCSkeleton width="75%" />
          </div>
        </div>
        <div className="rc-product-card__secondary">
          <div className="rc-product-card__details">
            <RCSkeleton width="0" />
          </div>
        </div>
      </div>
    </div>
  );
}

RCProductCard.Skeleton = Skeleton;
